'use client';

import BookshelvesDialog from './components/BookshelvesDialog';

import clsx from 'clsx';
import * as React from 'react';
import { MdChevronRight, MdClose } from 'react-icons/md';
import { useState, useRef, useEffect, Suspense, useCallback } from 'react';
import { ReadonlyURLSearchParams, useSearchParams } from 'next/navigation';

import { Book, BooksGroup, type LibrarySearchConfig } from '@/types/book';
import { AppService, DeleteAction } from '@/types/system';
import {
  buildBookLookupIndex,
  collectKnownSourcePaths,
  isInHiddenDir,
  normalizeFilePathForIndex,
  selectNewImportableFiles,
  toWatchedFolderImports,
} from '@/services/bookService';
import { debounce } from '@/utils/debounce';
import { createSerialRunner, runWithConcurrency } from '@/utils/concurrency';
import { createThrottledCheckpoint } from '@/utils/checkpoint';
import { DEFAULT_NEARBY_WORDS } from '@/utils/searchConfig';
import { clearLibrarySearchHistory, loadLibrarySearchHistory } from './utils/searchHistory';
import type { LibrarySearchTarget } from '@/types/book';
import { navigateToLibrary, navigateToLogin, navigateToReader } from '@/utils/nav';
import { splitLibraryOpenIds } from '@/utils/audiobook';
import { listFormater, markBookDeleted } from '@/utils/book';
import { getImportErrorMessage } from '@/services/errors';
import { ingestFile } from '@/services/ingestService';
import { saveBookMetadataEdit } from '@/services/bookMetadataEdit';
import { eventDispatcher } from '@/utils/event';
import { transferManager } from '@/services/transferManager';
import { purgeCloudBookData } from '@/services/purgeCloudBookData';
import { isMinervaCloudStorageActive } from '@/services/sync/cloudSyncProvider';
import { getFilename, getFolderImportGroupName, joinScannedPath } from '@/utils/path';
import {
  extractCollectionPack,
  isMinervaPackFile,
  resolveImportGroup,
} from '@/services/pack/packService';
import { parseOpenWithFiles } from '@/helpers/openWith';
import { getInitializedAppService, isTauriAppPlatform } from '@/services/environment';
import { checkForAppUpdates, checkAppReleaseNotes } from '@/helpers/updater';
import { impactFeedback } from '@tauri-apps/plugin-haptics';
import { getCurrentWebview } from '@tauri-apps/api/webview';
import { loadPersistedSession } from '@/services/sessionPersistence';

import { useEnv } from '@/context/EnvContext';
import { useAuth } from '@/context/AuthContext';
import { useThemeStore } from '@/store/themeStore';
import { useTranslation } from '@/hooks/useTranslation';
import { useLibraryStore } from '@/store/libraryStore';
import { useSettingsStore } from '@/store/settingsStore';
import { useResponsiveSize } from '@/hooks/useResponsiveSize';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import { useTheme } from '@/hooks/useTheme';
import { useUICSS } from '@/hooks/useUICSS';
import { useDemoBooks } from './hooks/useDemoBooks';
import { useBooksSync } from './hooks/useBooksSync';
import { useLibraryFileSync } from './hooks/useLibraryFileSync';
import { useBookTransferActions } from './hooks/useBookTransferActions';
import { useAbsOfflineDownload } from './hooks/useAbsOfflineDownload';
import { useAutoImportFolders } from './hooks/useAutoImportFolders';
import { useInboxDrainer } from '@/hooks/useInboxDrainer';
import { useOPDSSubscriptions } from '@/hooks/useOPDSSubscriptions';
import { useABSSync } from '@/hooks/useABSSync';
import { useBookDataStore } from '@/store/bookDataStore';
import { useTransferStore } from '@/store/transferStore';
import { useBackgroundTexture } from '@/hooks/useBackgroundTexture';
import { getLibraryViewSettings } from '@/helpers/settings';
import { useAppUrlIngress } from '@/hooks/useAppUrlIngress';
import { useOpenWithBooks } from '@/hooks/useOpenWithBooks';
import { useOpenLaunchLinks } from '@/hooks/useOpenLaunchLinks';
import { useHomeScreenWidgets } from '@/hooks/useHomeScreenWidgets';
import { useOpenShareLink } from '@/hooks/useOpenShareLink';
import { useOpenDeviceLink } from '@/hooks/useOpenDeviceLink';
import { useClipUrlIngress } from '@/hooks/useClipUrlIngress';
import { useWebBrowserDownloads } from '@/hooks/useWebBrowserDownloads';
import { useKeyDownActions } from '@/hooks/useKeyDownActions';
import { SelectedFile, resolveTauriFileName, useFileSelector } from '@/hooks/useFileSelector';
import { lockScreenOrientation, selectDirectory, showFilePicker } from '@/utils/bridge';
import { useAndroidPickedBooks } from '@/hooks/useAndroidFilePicker';
import { requestStoragePermission } from '@/utils/permission';
import { SUPPORTED_BOOK_EXTS } from '@/services/constants';
import {
  tauriHandleClose,
  tauriHandleSetAlwaysOnTop,
  tauriHandleToggleFullScreen,
  tauriQuitApp,
  tauriSetWindowTitle,
} from '@/utils/window';

import { getActiveBookshelfGroupBy } from '@/services/bookshelves/grouping';
import { LibraryGroupByType } from '@/types/settings';
import { BookMetadata } from '@/libs/document';
import { AboutWindow } from '@/components/AboutWindow';
import ReadingStatsDialog from '@/components/stats/ReadingStatsDialog';
import { KeyboardShortcutsHelp } from '@/components/KeyboardShortcutsHelp';
import LocalSendManager from '@/components/localsend/LocalSendManager';
import { BookDetailModal } from '@/components/metadata';
import { UpdaterWindow } from '@/components/UpdaterWindow';
import { CatalogDialog } from './components/OPDSDialog';
import { FeedsView } from './components/feeds/FeedsView';
import AddFeedModal from './components/feeds/AddFeedModal';
import { fetchAndParseFeed } from '@/services/rss/feedClient';
import { createFeedBook, ensureFeedBookCover } from '@/services/rss/feedBook';
import { MigrateDataWindow } from './components/MigrateDataWindow';
import { BackupWindow } from './components/BackupWindow';
import { CacheManagerWindow } from './components/CacheManagerWindow';
import { useDragDropImport } from './hooks/useDragDropImport';
import { useTransferQueue } from '@/hooks/useTransferQueue';
import { useAppRouter } from '@/hooks/useAppRouter';
import { Toast } from '@/components/Toast';
import { createBookGroups, findGroupById, getBreadcrumbs } from './utils/libraryUtils';
import Spinner from '@/components/Spinner';
import LibraryHeader from './components/LibraryHeader';
import Bookshelf from './components/Bookshelf';
import ImportMenuPopup from './components/ImportMenuPopup';
import GroupHeader from './components/GroupHeader';
import FailedImportsDialog, { FailedImport } from './components/FailedImportsDialog';
import ImportFromFolderDialog, {
  ImportFromFolderResult,
} from './components/ImportFromFolderDialog';
import WebSourcesDialog from './components/WebSourcesDialog';
import WebBrowserImportDialog from './components/WebBrowserImportDialog';
import ImportNovelDialog from './components/ImportNovelDialog';
import ImportPastedTextDialog from './components/ImportPastedTextDialog';
import NowPlayingBar from './components/NowPlayingBar';
import { convertToEpubWithWorker } from '@/services/send/conversion/conversionWorker';
import type { WebBrowserPage } from '@/services/webBrowser/webBrowser';
import ClipSignInAlert from '@/components/ClipSignInAlert';
import useShortcuts from '@/hooks/useShortcuts';
import { useReplicaPull } from '@/hooks/useReplicaPull';
import { useCustomFonts } from '@/hooks/useCustomFonts';
import DropIndicator from '@/components/DropIndicator';
import SettingsDialog from '@/components/settings/SettingsDialog';
import ModalPortal from '@/components/ModalPortal';
import TransferQueuePanel from './components/TransferQueuePanel';

/** Skip tiny non-book artifacts during folder auto-scan (matches the manual import dialog default). */
const AUTO_IMPORT_MIN_SIZE_BYTES = 20 * 1024;

const IMPORT_CONCURRENCY = 4;
// How often the library index is persisted during a long import (#5601). A
// crash/kill mid-run loses at most this much work instead of the entire run;
// keep it long enough that the full-library serialization stays a rounding
// error next to the per-file parse/copy work.
const IMPORT_CHECKPOINT_INTERVAL_MS = 15 * 1000;
// One import run at a time, app-wide: the manual Import-from-Folder flow and
// the watched-folder auto-scan must not interleave — each builds a lookup
// index over the same live library array, so overlapping runs re-import the
// same files as duplicate rows (#5601).
const enqueueImportRun = createSerialRunner();
const LIBRARY_SEARCH_MODES: LibrarySearchConfig['mode'][] = [
  'contains',
  'whole-words',
  'regex',
  'nearby-words',
  'fuzzy',
];

const getLibrarySearchConfig = (
  searchParams: ReadonlyURLSearchParams | null,
): LibrarySearchConfig => {
  const modeParam = searchParams?.get('mode') as LibrarySearchConfig['mode'] | null;
  const nearbyParam = Number(searchParams?.get('nearby'));
  return {
    scope: 'book',
    mode: modeParam && LIBRARY_SEARCH_MODES.includes(modeParam) ? modeParam : 'contains',
    matchCase: searchParams?.get('matchCase') === 'true',
    matchDiacritics: searchParams?.get('matchDiacritics') === 'true',
    nearbyWords:
      Number.isFinite(nearbyParam) && nearbyParam > 0 ? nearbyParam : DEFAULT_NEARBY_WORDS,
  };
};

/**
 * Key used to persist the last directory the user imported books from.
 * Stored in localStorage so re-opening the dialog (even across app
 * restarts) seeds the path field with their previous choice — this
 * mirrors the behaviour of native file pickers on most desktop OSes.
 */
const LAST_IMPORT_FOLDER_KEY = 'minerva:lastImportFolder';
/**
 * Key used to persist the user's last "Folder Structure" choice
 * ('keep' vs 'flatten'). Restored as the default radio selection on
 * the next dialog open.
 */
const LAST_IMPORT_FOLDER_MODE_KEY = 'minerva:lastImportFolderMode';
/**
 * Key used to persist the comma-separated list of FormatGroup ids the
 * user last ticked, e.g. "epub,pdf". Empty / missing falls back to the
 * dialog's built-in default ("epub,pdf").
 */
const LAST_IMPORT_FOLDER_FORMATS_KEY = 'minerva:lastImportFolderFormats';
/**
 * Key used to persist the last "File size larger than" threshold (KB).
 * Stored as a stringified non-negative integer.
 */
const LAST_IMPORT_FOLDER_MIN_SIZE_KEY = 'minerva:lastImportFolderMinSizeKB';
/**
 * Key used to persist the last "Read books in place" toggle value
 * (`'1'` or `'0'`). Restored as the dialog's initial toggle state.
 * The toggle only matters for fresh, not-yet-registered folders —
 * once a folder is registered as an external library folder, the
 * dialog forces the toggle ON regardless of this value.
 */
const LAST_IMPORT_FOLDER_READ_IN_PLACE_KEY = 'minerva:lastImportFolderReadInPlace';

const LibraryPageWithSearchParams = () => {
  const searchParams = useSearchParams();
  return <LibraryPageContent searchParams={searchParams} />;
};

const LibraryPageContent = ({ searchParams }: { searchParams: ReadonlyURLSearchParams | null }) => {
  const router = useAppRouter();
  const { envConfig, appService } = useEnv();
  const { token, user, login } = useAuth();
  const {
    library: libraryBooks,
    libraryLoaded: libraryLoadedFromDisk,
    isSyncing,
    syncProgress,
    updateBook,
    updateBooks,
    setLibrary,
    getGroupId,
    getGroupName,
    checkOpenWithBooks,
    checkLastOpenBooks,
    checkPendingLaunchLink,
    setCheckOpenWithBooks,
    setCheckLastOpenBooks,
  } = useLibraryStore();
  const _ = useTranslation();
  const { selectFiles } = useFileSelector(appService, _);
  const { safeAreaInsets: insets, isRoundedWindow } = useThemeStore();
  const { clearBookData } = useBookDataStore();
  const { settings, setSettings, saveSettings } = useSettingsStore();
  const { isSettingsDialogOpen, setSettingsDialogOpen } = useSettingsStore();
  // Field selector, not `const { isTransferQueueOpen } = useTransferStore()`:
  // a whole-store subscription re-renders the entire library tree on every
  // transfer progress tick (~10/sec per active upload), freezing the app
  // during a bulk cloud upload (issue #5047).
  const isTransferQueueOpen = useTransferStore((state) => state.isTransferQueueOpen);

  // Library page pulls user replicas (dictionaries, custom fonts,
  // background textures, OPDS catalogs, Audiobookshelf servers, custom
  // translators + prompts, custom TTS services, bundled settings). Deferred 10s; module-scoped
  // dedup means a later navigation to the reader won't re-pull the same kind.
  useReplicaPull({
    kinds: [
      'dictionary',
      'font',
      'texture',
      'opds_catalog',
      'abs_server',
      'custom_translator',
      'translation_prompt',
      'custom_tts',
      'settings',
      'bookshelf',
    ],
  });
  // Hydrate the custom-font store from persisted settings so the Font
  // panel sees imported fonts even when opened straight from the
  // library — the replica pull above is auth-gated and the reader's
  // FoliateViewer hydration never runs without a book open.
  useCustomFonts();
  const [showCatalogManager, setShowCatalogManager] = useState(
    searchParams?.get('opds') === 'true',
  );
  const [showFeeds, setShowFeeds] = useState(false);
  const [showAddFeed, setShowAddFeed] = useState(false);
  const [showWebSources, setShowWebSources] = useState(false);
  const [showImportNovel, setShowImportNovel] = useState(false);
  const [showImportPastedText, setShowImportPastedText] = useState(false);
  const [importMenuAnchor, setImportMenuAnchor] = useState<HTMLElement | null>(null);
  const [loading, setLoading] = useState(false);
  // Seed from the library store: if we already have books in memory (the
  // common reader → library return path), treat the page as loaded
  // immediately. This prevents `showBookshelf` from briefly being false on
  // remount, which used to flash a placeholder before `initLibrary` finished.
  const [libraryLoaded, setLibraryLoaded] = useState(() => libraryBooks.length > 0);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [isSelectNone, setIsSelectNone] = useState(false);
  const [librarySearchQuery, setLibrarySearchQuery] = useState(searchParams?.get('q') ?? '');
  const pendingLibrarySearchQueryRef = useRef<string | null>(null);
  const [librarySearchProgress, setLibrarySearchProgress] = useState<number | null>(null);
  const [librarySearchHistory, setLibrarySearchHistory] = useState<string[]>([]);
  const [librarySearchTarget, setLibrarySearchTarget] = useState<LibrarySearchTarget>(() =>
    ['contents', 'text'].includes(searchParams?.get('search') ?? '') ? 'text' : 'books',
  );
  const [librarySearchConfig, setLibrarySearchConfig] = useState<LibrarySearchConfig>(() =>
    getLibrarySearchConfig(searchParams),
  );
  useEffect(() => {
    if (librarySearchTarget === 'text' && !librarySearchQuery.trim()) {
      setLibrarySearchHistory(loadLibrarySearchHistory());
    }
  }, [librarySearchTarget, librarySearchQuery]);
  const librarySearchTargetRef = useRef(librarySearchTarget);
  const librarySearchConfigRef = useRef(librarySearchConfig);
  const [showDetailsBook, setShowDetailsBook] = useState<Book | null>(null);
  const [failedImportsModal, setFailedImportsModal] = useState<FailedImport[] | null>(null);
  // "Import from folder" dialog state. Held as a small object rather
  // than a boolean because we need a default starting directory to seed
  // the path field, and we want the dialog to remain mounted long
  // enough for the platform's folder picker to overlay it.
  const [importFromFolderState, setImportFromFolderState] = useState<{
    initialDirectory: string;
    initialFolderMode: 'keep' | 'flatten';
    initialSelectedGroupIds?: string[];
    initialMinSizeKB?: number;
    initialReadInPlace?: boolean;
    initialAutoImport?: boolean;
  } | null>(null);
  const [currentGroupPath, setCurrentGroupPath] = useState<string | undefined>(undefined);
  const [currentVirtualGroup, setCurrentVirtualGroup] = useState<{
    groupBy:
      | typeof LibraryGroupByType.Series
      | typeof LibraryGroupByType.Author
      | typeof LibraryGroupByType.Tag
      | typeof LibraryGroupByType.Subject
      | typeof LibraryGroupByType.Status;
    groupName: string;
    localized?: boolean;
  } | null>(null);
  // Direct (non-queued) download progress, keyed by book hash. Entries are
  // added and removed by useBookTransferActions, its only writer.
  const [booksTransferProgress, setBooksTransferProgress] = useState<{
    [key: string]: number;
  }>({});
  const [pendingNavigationBookIds, setPendingNavigationBookIds] = useState<string[] | null>(null);
  const isInitiating = useRef(false);

  const iconSize = useResponsiveSize(18);
  const viewSettings = settings.globalViewSettings;
  const demoBooks = useDemoBooks();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const handleScrollerRef = useCallback((el: HTMLDivElement | null) => {
    scrollRef.current = el;
  }, []);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  // Tracks paths that failed to import in this session so auto-import does not
  // re-attempt (and re-toast) them on every subsequent folder scan.
  const autoImportFailedPathsRef = useRef<Set<string>>(new Set());
  // Folders whose fs/asset scopes were already granted this session. Each
  // `allowPathsInScopes` call makes tauri-plugin-persisted-scope rewrite its
  // whole state file on the main thread, so grant once, not on every focus
  // scan (issue #5494).
  const autoImportGrantedFoldersRef = useRef<Set<string>>(new Set());

  const getScrollKey = (group: string) => `library-scroll-${group || 'all'}`;

  const saveScrollPosition = (group: string) => {
    if (scrollRef.current) {
      sessionStorage.setItem(getScrollKey(group), scrollRef.current.scrollTop.toString());
    }
  };

  const restoreScrollPosition = useCallback((group: string) => {
    const savedPosition = sessionStorage.getItem(getScrollKey(group));
    if (savedPosition && scrollRef.current) {
      scrollRef.current.scrollTop = parseInt(savedPosition, 10);
    }
  }, []);

  useTheme({ systemUIVisible: true, appThemeColor: 'base-200' });
  useUICSS();

  // Apply the library's own background texture (separate from the reader's,
  // issue #4743). Re-applies on mount so returning from a textured book
  // restores the library background, and whenever the library texture — or the
  // reader/global texture it inherits when unset — changes from the Color panel.
  const { applyBackgroundTexture } = useBackgroundTexture();
  useEffect(() => {
    applyBackgroundTexture(envConfig, getLibraryViewSettings(settings));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    envConfig,
    applyBackgroundTexture,
    settings.libraryBackgroundTextureId,
    settings.libraryBackgroundOpacity,
    settings.libraryBackgroundSize,
    settings.globalViewSettings?.backgroundTextureId,
    settings.globalViewSettings?.backgroundOpacity,
    settings.globalViewSettings?.backgroundSize,
  ]);

  useAppUrlIngress();
  useOpenWithBooks();
  useOpenLaunchLinks();
  useHomeScreenWidgets();
  useOpenShareLink();
  useOpenDeviceLink();
  useClipUrlIngress();
  const {
    pending: pendingWebBrowserDownload,
    confirm: confirmWebBrowserDownload,
    dismiss: dismissWebBrowserDownload,
  } = useWebBrowserDownloads();
  useTransferQueue(libraryLoaded);

  const { pullLibrary, pushLibrary } = useBooksSync();
  // Library-scoped auto-sync for the active third-party cloud provider (WebDAV /
  // Google Drive): keeps library.json current on import / delete / book-close,
  // parity with useBooksSync. No-op when no provider is enabled.
  useLibraryFileSync();
  const { checkOPDSSubscriptions } = useOPDSSubscriptions();
  useABSSync();
  useInboxDrainer();
  const { isDragging } = useDragDropImport();

  usePullToRefresh(
    scrollRef,
    async () => {
      if (!user) {
        navigateToLogin(router);
        return;
      }
      await pullLibrary(false, true);
      checkOPDSSubscriptions(true);
    },
    async () => {
      if (!user) {
        navigateToLogin(router);
        return;
      }
      await pullLibrary(true, true);
      checkOPDSSubscriptions(true);
    },
  );
  useShortcuts({
    onToggleFullscreen: () => {
      if (!isTauriAppPlatform()) return false;
      return tauriHandleToggleFullScreen().then(() => true);
    },
    onCloseWindow: () => {
      if (!isTauriAppPlatform()) return false;
      return tauriHandleClose().then(() => true);
    },
    onQuitApp: () => {
      if (!isTauriAppPlatform()) return false;
      return tauriQuitApp().then(() => true);
    },
    onOpenFontLayoutSettings: () => {
      setSettingsDialogOpen(true);
    },
    onOpenBooks: () => {
      handleImportBooksFromFiles();
    },
  });

  useEffect(() => {
    const snapshot = searchParams?.toString() || '';
    if (snapshot !== new URLSearchParams(window.location.search).toString()) return;
    sessionStorage.setItem('lastLibraryParams', snapshot);
  }, [searchParams]);

  // Strip the empty `group=` param that `handleLibraryNavigation` sets as a
  // workaround for a Next.js 16.2 static-export regression (see the NOTE
  // above `handleLibraryNavigation` for full context). This effect runs
  // after the router.replace() has committed, so React has already
  // re-rendered with the new (empty) group state; we're only rewriting the
  // URL cosmetically via window.history.replaceState — Next.js' patched
  // replaceState will pick up the new canonical URL without triggering
  // another navigation.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (searchParams?.get('group') !== '') return;
    const url = new URL(window.location.href);
    url.searchParams.delete('group');
    const cleanHref = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(null, '', cleanHref);
  }, [searchParams]);

  // Unified navigation function that handles scroll position and direction.
  // Workaround for a Next.js 16.2 static-export regression: navigating to a
  // same-pathname URL with an empty search string causes `router.replace()`
  // to silently no-op (e.g. `/library?group=foo` -> `/library`), which broke
  // the breadcrumb "All" button. By always calling `params.set('group',
  // targetGroup)` — including when `targetGroup` is an empty string — the
  // resulting URL becomes `/library?group=` instead of `/library`, which
  // Next.js does commit. The trailing empty `group=` is stripped via a
  // cleanup effect below (purely cosmetic URL rewrite). See
  // https://github.com/readest/readest/issues/3782.
  const handleLibraryNavigation = useCallback(
    (targetGroup: string, shelfId?: string) => {
      const params = new URLSearchParams(window.location.search);
      const currentGroup = params.get('group') || '';

      // Save current scroll position BEFORE navigation
      saveScrollPosition(currentGroup);

      // Detect and set navigation direction
      const direction = currentGroup && !targetGroup ? 'back' : 'forward';
      document.documentElement.setAttribute('data-nav-direction', direction);

      // Build query params — always `set` so the search string is non-empty
      // even when targetGroup is '' (the Next.js 16.2 workaround).
      params.set('group', targetGroup);
      if (!targetGroup) params.delete('shelf');
      else if (shelfId) params.set('shelf', shelfId);

      navigateToLibrary(router, `${params.toString()}`);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router],
  );

  const handleBackUpOneGroupLevel = () => {
    if (!currentGroupPath) return;
    const segments = currentGroupPath.split('/');
    const parentPath = segments.length > 1 ? segments.slice(0, -1).join('/') : undefined;
    const parentGroupId = parentPath ? getGroupId(parentPath) || '' : '';
    setIsSelectAll(false);
    setIsSelectNone(false);
    handleLibraryNavigation(parentGroupId);
  };

  const handleBackUpOneGroupLevelRef = useRef(handleBackUpOneGroupLevel);
  handleBackUpOneGroupLevelRef.current = handleBackUpOneGroupLevel;
  const triggerBackUpOneGroupLevel = useCallback(() => handleBackUpOneGroupLevelRef.current(), []);

  useKeyDownActions({
    onCancel: triggerBackUpOneGroupLevel,
    enabled: !!appService?.isAndroidApp && !!currentGroupPath,
  });

  useEffect(() => {
    const doCheckAppUpdates = async () => {
      if (appService?.hasUpdater && settings.autoCheckUpdates) {
        await checkForAppUpdates(_, true, settings.updateChannel);
      } else if (appService?.hasUpdater === false) {
        checkAppReleaseNotes();
      }
    };
    if (settings.alwaysOnTop) {
      tauriHandleSetAlwaysOnTop(settings.alwaysOnTop);
    }
    doCheckAppUpdates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appService?.hasUpdater, settings]);

  useEffect(() => {
    if (appService?.isMobileApp) {
      lockScreenOrientation({ orientation: 'auto' });
    }
  }, [appService]);

  // Drop the book name the reader put in the window title, so a window back on
  // the library does not keep announcing a book that is no longer open.
  useEffect(() => {
    if (appService?.hasWindow) {
      tauriSetWindowTitle();
    }
  }, [appService?.hasWindow]);

  useEffect(() => {
    if (appService?.hasWindow) {
      const currentWebview = getCurrentWebview();
      const unlisten = currentWebview.listen('close-reader-window', async () => {
        // Reader windows are independent Tauri webviews with their own
        // libraryStore instance — progress / readingStatus / move-to-front
        // updates from the reader window do NOT propagate to this main
        // window's store. Reload from disk so the library reflects the
        // changes the reader just persisted.
        const appService = await envConfig.getAppService();
        const settings = await appService.loadSettings();
        const library = await appService.loadLibraryBooks();
        setSettings(settings);
        setLibrary(library);
      });
      return () => {
        unlisten.then((fn) => fn());
      };
    }
    return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appService, envConfig]);

  const handleImportBookFiles = useCallback(async (event: CustomEvent) => {
    const selectedFiles: SelectedFile[] = event.detail.files;
    const groupId: string = event.detail.groupId || '';
    if (selectedFiles.length === 0) return;
    await importBooks(selectedFiles, groupId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleImportBookDirectory = useCallback(async (event: CustomEvent) => {
    const dirPath: string | undefined = event.detail?.path;
    if (!dirPath) return;
    await handleImportBooksFromDirectory(dirPath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    eventDispatcher.on('import-book-files', handleImportBookFiles);
    eventDispatcher.on('import-book-directory', handleImportBookDirectory);
    return () => {
      eventDispatcher.off('import-book-files', handleImportBookFiles);
      eventDispatcher.off('import-book-directory', handleImportBookDirectory);
    };
  }, [handleImportBookFiles, handleImportBookDirectory]);

  useEffect(() => {
    if (!libraryBooks.some((book) => !book.deletedAt)) {
      handleSetSelectMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryBooks]);

  const processOpenWithFiles = useCallback(
    async (appService: AppService, openWithFiles: string[], libraryBooks: Book[]) => {
      const settings = await appService.loadSettings();
      const bookIds: string[] = [];
      for (const file of openWithFiles) {
        console.log('Open with book:', file);
        try {
          // An Android VIEW intent hands over a content:// URI with no extension.
          if (isMinervaPackFile(await resolveTauriFileName(file, appService))) {
            const raw = await appService.readFile(file, 'None', 'binary');
            const pack = typeof raw === 'string' ? null : await extractCollectionPack(raw);
            if (pack) {
              for (const { file: innerFile, groupName: packGroupName } of pack.books) {
                const { groupId, groupName } = resolveImportGroup(
                  undefined,
                  packGroupName,
                  getGroupId,
                  getGroupName,
                );
                try {
                  const book = await ingestFile(
                    {
                      file: innerFile,
                      books: libraryBooks,
                      transient: false,
                      groupId,
                      groupName,
                      forceUpload: !!appService.isMobile && !!user,
                    },
                    { appService, settings, isLoggedIn: !!user },
                  );
                  if (book && bookIds.length === 0) {
                    bookIds.push(book.hash);
                  }
                } catch (err) {
                  console.warn(
                    'Failed to import pack entry during open-with:',
                    innerFile.name,
                    err,
                  );
                }
              }
              continue;
            }
          }

          const temp = appService.isMobile ? false : !settings.autoImportBooksOnOpen;
          // A file shared into Minerva on mobile (the OS share-sheet) is a
          // "Send to Minerva" capture — force it to the cloud so it syncs to
          // every device. Desktop "open with" honors the book sync toggle.
          const book = await ingestFile(
            {
              file,
              books: libraryBooks,
              transient: temp,
              forceUpload: !!appService.isMobile && !!user,
            },
            { appService, settings, isLoggedIn: !!user },
          );
          if (book) {
            bookIds.push(book.hash);
          }
        } catch (error) {
          console.log('Failed to import book:', file, error);
        }
      }
      setLibrary(libraryBooks);
      appService.saveLibraryBooks(libraryBooks);

      console.log('Opening books:', bookIds);
      if (bookIds.length > 0) {
        setPendingNavigationBookIds(bookIds);
        return true;
      }
      return false;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleOpenLastBooks = async (
    appService: AppService,
    lastBookIds: string[],
    libraryBooks: Book[],
  ) => {
    if (lastBookIds.length === 0) return false;
    const bookIds: string[] = [];
    for (const bookId of lastBookIds) {
      const book = libraryBooks.find((b) => b.hash === bookId && b.readingStatus !== 'finished');
      if (book && (await appService.isBookAvailable(book))) {
        bookIds.push(book.hash);
      }
    }
    console.log('Opening last books:', bookIds);
    if (bookIds.length > 0) {
      setPendingNavigationBookIds(bookIds);
      return true;
    }
    return false;
  };

  const handleShowFeeds = () => {
    setShowAddFeed(true);
  };

  const handleAddFeedSubmit = async (url: string) => {
    const parsed = await fetchAndParseFeed(url);
    const book = createFeedBook(url, parsed);
    if (appService) {
      book.coverImageUrl = await ensureFeedBookCover(appService, book);
    }
    await useLibraryStore.getState().updateBooks(envConfig, [book]);
    eventDispatcher.dispatch('toast', {
      type: 'success',
      message: _('Subscribed to "{{title}}"', { title: book.title }),
      timeout: 3000,
    });
  };

  const handleShowOPDSDialog = () => {
    setShowCatalogManager(true);
  };

  const handleDismissOPDSDialog = () => {
    setShowCatalogManager(false);
    const params = new URLSearchParams(window.location.search);
    params.delete('opds');
    navigateToLibrary(router, `${params.toString()}`);
  };

  const libraryInitKey = (() => {
    const params = new URLSearchParams(searchParams?.toString());
    for (const key of ['q', 'search', 'mode', 'matchCase', 'matchDiacritics', 'nearby']) {
      params.delete(key);
    }
    return params.toString();
  })();

  useEffect(() => {
    if (pendingNavigationBookIds) {
      const bookIds = pendingNavigationBookIds;
      setPendingNavigationBookIds(null);
      if (bookIds.length > 0) {
        const { audiobookHash, readerIds, droppedAudiobooks } = splitLibraryOpenIds(
          bookIds,
          (hash) => libraryBooks.find((book) => book.hash === hash),
        );
        if (audiobookHash) {
          router.push(`/player?id=${audiobookHash}`);
          return;
        }
        if (droppedAudiobooks) {
          eventDispatcher.dispatch('toast', {
            message: _('Audiobooks open in the player'),
            type: 'info',
          });
        }
        if (readerIds.length > 0) {
          navigateToReader(router, readerIds);
        }
      }
    }
  }, [pendingNavigationBookIds, appService, router, libraryBooks]);

  useEffect(() => {
    if (isInitiating.current) return;
    isInitiating.current = true;

    const initLogin = async () => {
      const appService = await envConfig.getAppService();
      const settings = await appService.loadSettings();
      let currentToken = token;
      let currentUser = user;
      if (!currentToken || !currentUser) {
        const persisted = await loadPersistedSession();
        if (persisted && persisted.token && persisted.user) {
          login(persisted.token, persisted.user as unknown as import('@supabase/supabase-js').User);
          currentToken = persisted.token;
          currentUser = persisted.user as unknown as import('@supabase/supabase-js').User;
        }
      }
      if (currentToken && currentUser) {
        if (!settings.keepLogin) {
          settings.keepLogin = true;
          setSettings(settings);
          saveSettings(envConfig, settings);
        }
      } else if (settings.keepLogin) {
        router.push('/auth');
      }
    };

    // Reuse the in-store library only when it was actually loaded from disk.
    // Gating on `length > 0` was unsafe: a transient "Open with" entry made the
    // store non-empty before any disk load, so this skipped loadLibraryBooks and
    // a later save persisted the partial library (wiping library.json).
    const hasCachedLibrary = libraryLoadedFromDisk;
    const loadingTimeout = hasCachedLibrary ? null : setTimeout(() => setLoading(true), 500);
    const initLibrary = async () => {
      const appService = await envConfig.getAppService();
      const settings = await appService.loadSettings();
      setSettings(settings);

      // Re-grant fs_scope / asset_protocol_scope for every external
      // library folder the user registered in a previous session, so
      // in-place books under those roots are immediately readable
      // through both `dir_scanner::read_dir` and the fs plugin.
      // Best-effort — `allowPathsInScopes` swallows its own errors.
      // On iOS the corresponding native-bridge plugin separately
      // re-acquires security-scoped resources via persisted
      // bookmarks (see InPlaceFolderBookmarkStore in
      // NativeBridgePlugin.swift); here we just sync Tauri's in-memory
      // scope set with the persisted intent.
      const externalRoots = settings.externalLibraryFolders ?? [];
      if (externalRoots.length > 0 && appService.allowPathsInScopes) {
        await appService.allowPathsInScopes(externalRoots, true);
      }

      // Reuse the library from the store when we return from the reader
      const library = hasCachedLibrary ? libraryBooks : await appService.loadLibraryBooks();
      let opened = false;
      if (checkOpenWithBooks) {
        opened = await handleOpenWithBooks(appService, library);
      }
      setCheckOpenWithBooks(opened);
      if (!opened && checkLastOpenBooks && settings.openLastBooks) {
        opened = await handleOpenLastBooks(appService, settings.lastOpenBooks, library);
      }
      setCheckLastOpenBooks(opened);

      // Skip the redundant setLibrary on the cached path: the store already
      // contains the same array reference, and a no-op set would still
      // trigger refreshGroups (O(n) MD5) and a full Bookshelf re-render.
      // The cold path or the openWith / openLast path may have produced a
      // different `library` reference (intent-imported books) — only then
      // do we commit it.
      if (!hasCachedLibrary || library !== libraryBooks) {
        setLibrary(library);
      }
      setLibraryLoaded(true);
      if (loadingTimeout) clearTimeout(loadingTimeout);
      setLoading(false);
    };

    const handleOpenWithBooks = async (appService: AppService, library: Book[]) => {
      const openWithFiles = (await parseOpenWithFiles(appService)) || [];

      if (openWithFiles.length > 0) {
        return await processOpenWithFiles(appService, openWithFiles, library);
      }
      return false;
    };

    // Both inits are fire-and-forget, and `checkOpenWithBooks` /
    // `checkLastOpenBooks` are cleared only on `initLibrary`'s success path.
    // An escaping throw therefore left this page's early return rendering a
    // bare `full-height` div for the rest of the session — the blank App Store
    // window, where the sandbox denied a stale `customRootDir` and the mkdir
    // inside `loadLibraryBooks` rejected with nothing to catch it. Always
    // release the render gates, then say what actually broke.
    const recoverFromInitFailure = (error: unknown) => {
      console.error('Failed to initialize library:', error);
      setCheckOpenWithBooks(false);
      setCheckLastOpenBooks(false);
      // A launch link waiting on the library would otherwise hold the page blank for good.
      useLibraryStore.getState().setCheckPendingLaunchLink(false);
      setLibraryLoaded(true);
      if (loadingTimeout) clearTimeout(loadingTimeout);
      setLoading(false);
      const unavailableRootDir = getInitializedAppService()?.unavailableRootDir;
      eventDispatcher.dispatch('toast', {
        type: 'error',
        message: unavailableRootDir
          ? _(
              'Cannot open the library folder "{{path}}". Reconnect it, or choose another folder in Settings.',
              { path: unavailableRootDir },
            )
          : _('Failed to load your library.'),
        timeout: 10000,
      });
    };

    initLogin().catch((error) => console.error('Failed to initialize login:', error));
    initLibrary().catch(recoverFromInitFailure);
    return () => {
      setCheckOpenWithBooks(false);
      setCheckLastOpenBooks(false);
      isInitiating.current = false;
    };
    // Non-search URL changes trigger parsing OPEN_WITH_FILES without reinitializing on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryInitKey]);

  useEffect(() => {
    const group = searchParams?.get('group') || '';
    const groupName = getGroupName(group);
    setCurrentGroupPath(groupName);
  }, [libraryBooks, searchParams, getGroupName]);

  useEffect(() => {
    if (
      (searchParams?.toString() || '') !== new URLSearchParams(window.location.search).toString()
    ) {
      return;
    }
    const urlQuery = searchParams?.get('q') ?? '';
    if (pendingLibrarySearchQueryRef.current === urlQuery) {
      pendingLibrarySearchQueryRef.current = null;
    }
    if (pendingLibrarySearchQueryRef.current === null) setLibrarySearchQuery(urlQuery);
    const target = ['contents', 'text'].includes(searchParams?.get('search') ?? '')
      ? 'text'
      : 'books';
    const config = getLibrarySearchConfig(searchParams);
    librarySearchTargetRef.current = target;
    librarySearchConfigRef.current = config;
    setLibrarySearchTarget(target);
    setLibrarySearchConfig(config);
  }, [searchParams]);

  useEffect(() => {
    const group = searchParams?.get('group') || '';
    restoreScrollPosition(group);
  }, [searchParams, restoreScrollPosition]);

  // Track the current virtual group for the navigation header.
  useEffect(() => {
    const groupId = searchParams?.get('group') || '';
    const groupBy = getActiveBookshelfGroupBy(settings, searchParams);

    if (
      groupId &&
      (groupBy === LibraryGroupByType.Series ||
        groupBy === LibraryGroupByType.Author ||
        groupBy === LibraryGroupByType.Tag ||
        groupBy === LibraryGroupByType.Subject ||
        groupBy === LibraryGroupByType.Status)
    ) {
      // Find the group to get its name
      const allGroups = createBookGroups(
        libraryBooks.filter((b) => !b.deletedAt),
        groupBy,
      );
      const targetGroup = findGroupById(allGroups, groupId);

      if (targetGroup) {
        setCurrentVirtualGroup({
          groupBy,
          groupName: targetGroup.displayName || targetGroup.name,
          localized: targetGroup.localized,
        });
      } else {
        setCurrentVirtualGroup(null);
      }
    } else {
      setCurrentVirtualGroup(null);
    }
  }, [libraryBooks, searchParams, settings.libraryGroupBy, settings.bookshelves]);

  useEffect(() => {
    if (demoBooks.length > 0 && libraryLoaded) {
      const newLibrary = [...libraryBooks];
      for (const book of demoBooks) {
        const idx = newLibrary.findIndex((b) => b.hash === book.hash);
        if (idx === -1) {
          newLibrary.push(book);
        } else {
          newLibrary[idx] = book;
        }
      }
      setLibrary(newLibrary);
      appService?.saveLibraryBooks(newLibrary);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoBooks, libraryLoaded]);

  const importBooks = (
    files: SelectedFile[],
    groupId?: string,
    options: { silent?: boolean } = {},
  ): Promise<{ failedPaths: string[]; importedCount: number }> =>
    // Whole runs are serialized (see enqueueImportRun); the lookup index is
    // built inside the run so a queued run sees everything the previous one
    // imported.
    enqueueImportRun(() => importBooksRun(files, groupId, options));

  const importBooksRun = async (
    files: SelectedFile[],
    groupId?: string,
    options: { silent?: boolean } = {},
  ): Promise<{ failedPaths: string[]; importedCount: number }> => {
    setLoading(true);
    const { library } = useLibraryStore.getState();
    // Build the lookup index ONCE per import batch so each book lookup is
    // O(1) instead of O(n) over the existing library. importBook also keeps
    // the index updated as new books are appended, so subsequent files in
    // the same batch see the additions.
    //
    // `osPlatform` is required for the `byFilePath` arm: on case-insensitive
    // filesystems (macOS / iOS / Windows) two paths that differ only in
    // casing must hash to the same key, so the in-place fast path in
    // importBook can recognize a re-import of the same file.
    const lookupIndex = buildBookLookupIndex(library, appService?.osPlatform);
    const failedImports: Array<{ filename: string; errorMessage: string }> = [];
    const failedPaths: string[] = [];
    const successfulImports: string[] = [];

    // Minerva's own Books/ prefix is resolved once at app init and persisted
    // in `settings.localBooksDir`. We hand it to `ingestFile` so the in-place
    // decision can exclude files that already live inside our managed hash
    // store WITHOUT misclassifying user-owned folders that happen to be
    // named "Books" (e.g. Baidu Netdisk's default `Books/` directory
    // directly under the user's library root).
    const appBooksPrefix: string | null =
      useSettingsStore.getState().settings.localBooksDir || null;

    // A pack expands into its books, which land in their own groups unless the
    // import already targets a group.
    const effectiveFiles: (SelectedFile & { packGroupName?: string })[] = [];
    for (const selectedFile of files) {
      const fileName = selectedFile.name || selectedFile.file?.name || selectedFile.path || '';
      if (appService && isMinervaPackFile(fileName)) {
        try {
          const data = selectedFile.file
            ? await selectedFile.file.arrayBuffer()
            : await appService.readFile(selectedFile.path!, 'None', 'binary');
          const pack = typeof data === 'string' ? null : await extractCollectionPack(data);
          if (pack) {
            for (const { file, groupName } of pack.books) {
              effectiveFiles.push({
                file,
                name: file.name,
                packGroupName: groupId ? undefined : groupName,
              });
            }
            continue;
          }
        } catch (err) {
          console.warn('Failed to unpack archive during import:', err);
        }
      }
      effectiveFiles.push(selectedFile);
    }

    const processFile = async (
      selectedFile: (typeof effectiveFiles)[number],
    ): Promise<Book | null> => {
      const file = selectedFile.file || selectedFile.path;
      if (!file) return null;
      if (!appService) return null;
      try {
        const { path, basePath } = selectedFile;
        // `groupId` is treated as a tri-state:
        //   - undefined  → caller didn't specify; derive grouping from
        //                  basePath (Import-from-Folder "keep" mode).
        //   - '' (empty) → caller explicitly wants the library root.
        //   - any string → caller explicitly wants that group.
        // Distinguishing '' from undefined matters for re-imports of an
        // already-known book: without it, a falsy check would silently
        // keep the existingBook's stale groupId/groupName from a prior
        // import instead of moving the book to the root.
        let { groupId: resolvedGroupId, groupName: resolvedGroupName } = resolveImportGroup(
          groupId,
          selectedFile.packGroupName,
          getGroupId,
          getGroupName,
        );
        if (resolvedGroupId === undefined && path && basePath) {
          resolvedGroupName = getFolderImportGroupName(path, basePath);
          resolvedGroupId = getGroupId(resolvedGroupName);
        }
        // Read settings from the store at call-time rather than the
        // component closure. `runFolderImport` may have just registered
        // the picked directory as an external library folder via
        // `setSettings(...)`, but React state updates don't mutate the
        // already-captured `settings` reference until the next render —
        // by the time we get here, the closure still holds the *old*
        // settings, so `shouldImportInPlace` would see an empty
        // `externalLibraryFolders` and incorrectly fall back to copy
        // mode. Pulling the latest snapshot from zustand fixes this.
        const liveSettings = useSettingsStore.getState().settings;
        const book = await ingestFile(
          {
            file,
            books: library,
            lookupIndex,
            groupId: resolvedGroupId,
            groupName: resolvedGroupName,
          },
          { appService, settings: liveSettings, isLoggedIn: !!user, appBooksPrefix },
        );
        if (!book) return null;
        successfulImports.push(book.title);
        return book;
      } catch (error) {
        const filename = typeof file === 'string' ? file : file.name;
        if (typeof file === 'string') failedPaths.push(file);
        const baseFilename = getFilename(filename);
        const errorMessage = error instanceof Error ? _(getImportErrorMessage(error.message)) : '';
        failedImports.push({ filename: baseFilename, errorMessage });
        console.error('Failed to import book:', filename, error);
        return null;
      }
    };

    // Periodically persist the library index while the run is in flight so a
    // crash/kill mid-import (#5601: WebKit resource exhaustion during bulk
    // TXT folder import) loses at most one interval of work instead of the
    // whole run — the book dirs are written per file, and index rows that
    // never reach disk are what re-imports and duplicate rows are made of.
    // Saving per book would bring back the "library.json save dominates large
    // imports" cost, hence the throttle.
    const checkpoint = createThrottledCheckpoint(async () => {
      const currentLibrary = useLibraryStore.getState().library;
      const currentAppService = await envConfig.getAppService();
      await currentAppService.saveLibraryBooks(currentLibrary);
    }, IMPORT_CHECKPOINT_INTERVAL_MS);

    // A true worker pool (not the old barrier batches of 4): a slow file no
    // longer stalls three finished siblings, and each completed book streams
    // into the store immediately so the shelf renders incrementally.
    await runWithConcurrency(effectiveFiles, IMPORT_CONCURRENCY, async (selectedFile) => {
      const book = await processFile(selectedFile);
      if (book) {
        await updateBooks(envConfig, [book], { skipSave: true });
        checkpoint.touch();
      }
    });

    // Persist whatever the last checkpoint hasn't covered.
    if (successfulImports.length > 0) {
      await checkpoint.flush();
    }

    pushLibrary();

    if (!options.silent && failedImports.length > 1) {
      setFailedImportsModal(failedImports);
    } else if (!options.silent && failedImports.length === 1) {
      const { filename, errorMessage } = failedImports[0]!;
      eventDispatcher.dispatch('toast', {
        message:
          _('Failed to import book(s): {{filenames}}', {
            filenames: listFormater(false).format([filename]),
          }) + (errorMessage ? `\n${errorMessage}` : ''),
        timeout: 5000,
        type: 'error',
      });
    }
    // Surface the success toast when books were imported. In silent (auto-import)
    // mode failures are suppressed, so show success independently of them; in
    // interactive mode keep the original behaviour (only when nothing failed).
    if (successfulImports.length > 0 && (options.silent || failedImports.length === 0)) {
      eventDispatcher.dispatch('toast', {
        message: _('Successfully imported {{count}} book(s)', {
          count: successfulImports.length,
        }),
        timeout: 2000,
        type: 'success',
      });
    }

    setLoading(false);
    return { failedPaths, importedCount: successfulImports.length };
  };

  /**
   * Re-scan the given watched folders (the user's `autoImportFolders`) and
   * import any newly-added books. Reuses the same in-place import + dedup as
   * manual folder import, but stays quiet: unreadable folders are skipped (no
   * toast), and `importBooks` runs only when genuinely-new files exist (its
   * success toast then fires).
   */
  const autoImportFromWatchedFolders = async (folders: string[]) => {
    if (!appService || loading) return;
    const { library } = useLibraryStore.getState();
    const osPlatform = appService.osPlatform;
    // Known local source paths — live AND soft-deleted (files the user deleted
    // but whose in-place source is still on disk), plus paths that already failed
    // to import this session — so we neither resurrect a deleted book nor
    // re-parse/re-toast a bad file on every focus.
    const existingPaths = collectKnownSourcePaths(library, osPlatform);
    for (const key of autoImportFailedPathsRef.current) existingPaths.add(key);
    const newFiles: SelectedFile[] = [];
    const watchedExts = SUPPORTED_BOOK_EXTS.filter((ext) => ext !== 'rbooks');
    for (const folder of folders) {
      try {
        if (!autoImportGrantedFoldersRef.current.has(folder)) {
          await appService.allowPathsInScopes?.([folder], true);
          autoImportGrantedFoldersRef.current.add(folder);
        }
        const items = await appService.readDirectory(folder, 'None', watchedExts);
        const entries = items
          .filter((item) => !isInHiddenDir(item.path))
          .map((item) => ({
            fullPath: joinScannedPath(folder, item.path),
            size: item.size,
          }));
        const fresh = selectNewImportableFiles(entries, {
          extensions: watchedExts,
          minSizeBytes: AUTO_IMPORT_MIN_SIZE_BYTES,
          existingPaths,
          osPlatform,
        });
        // Reproduce the folder's own "Folder Structure" choice: unless it was
        // imported flat, each file carries the watched folder as `basePath` so
        // `importBooks` seats the book in the group its subfolder implies —
        // the same group the folder's initial import used (issue #5423).
        newFiles.push(
          ...toWatchedFolderImports(folder, fresh, isFlattenedAutoImportFolder(folder)),
        );
        for (const entry of fresh) {
          // Prevent the same file matching again via a later overlapping folder.
          const key = normalizeFilePathForIndex(entry.fullPath, osPlatform);
          if (key) existingPaths.add(key);
        }
      } catch (e) {
        // One unreadable/temporarily-missing folder must not abort the others
        // or nag the user (unlike the manual path, which nudges a re-pick).
        console.error('Auto-import: failed to scan folder', folder, e);
      }
    }
    if (newFiles.length > 0) {
      const { failedPaths } = await importBooks(newFiles, undefined, { silent: true });
      for (const p of failedPaths) {
        const key = normalizeFilePathForIndex(p, osPlatform);
        if (key) autoImportFailedPathsRef.current.add(key);
      }
    }
  };

  // Local-folder counterpart of useLibraryFileSync: re-scan the folders the
  // user opted into auto-import (a subset of externalLibraryFolders, chosen
  // per-folder in the Import-from-Folder dialog) and import newly-added books
  // on library open and app focus. Desktop + Android only (iOS security-scoped
  // bookmarks are out of scope).
  useAutoImportFolders({
    enabled:
      (settings.autoImportFolders?.length ?? 0) > 0 &&
      libraryLoaded &&
      isTauriAppPlatform() &&
      !appService?.isIOSApp,
    folders: settings.autoImportFolders ?? [],
    scanAndImport: autoImportFromWatchedFolders,
  });

  // Queue downloads (the TransferQueuePanel path) report progress into the
  // transfer store instead of through this hook. Bookshelf reads them straight
  // from the store via `selectActiveBookDownloadProgress` and merges them with
  // this state, so `booksTransferProgress` keeps a single writer.
  const { handleBookUpload, handleBookDownload } = useBookTransferActions(
    envConfig,
    appService,
    updateBook,
    setBooksTransferProgress,
  );

  const handleBookDelete = (deleteAction: DeleteAction) => {
    return async (book: Book, syncBooks = true) => {
      const deletionMessages = {
        both: _('Book deleted: {{title}}', { title: book.title }),
        cloud: _('Deleted cloud backup of the book: {{title}}', { title: book.title }),
        local: _('Deleted local copy of the book: {{title}}', { title: book.title }),
        purge: _('Purged book data: {{title}}', { title: book.title }),
      };
      const deletionFailMessages = {
        both: _('Failed to delete book: {{title}}', { title: book.title }),
        cloud: _('Failed to delete cloud backup of the book: {{title}}', { title: book.title }),
        local: _('Failed to delete local copy of the book: {{title}}', { title: book.title }),
        purge: _('Failed to purge book data: {{title}}', { title: book.title }),
      };

      try {
        // Purge also erases the book's synced progress and notes, or the next
        // open pulls them straight back (#6532). It runs first: if the network
        // step fails, nothing irreversible has happened locally yet.
        if (deleteAction === 'purge' && user) {
          await purgeCloudBookData(book.hash);
        }

        // Handle local deletion immediately. Purge mirrors 'both' (tombstone +
        // queued cloud delete) but hands 'purge' to deleteBook, which also wipes
        // the entire Books/<hash>/ folder (config/nav/cover) — issue #4615.
        if (deleteAction === 'local' || deleteAction === 'both' || deleteAction === 'purge') {
          await appService?.deleteBook(book, deleteAction === 'purge' ? 'purge' : 'local');
          if (deleteAction === 'both' || deleteAction === 'purge') {
            // The tombstone also bumps `updatedAt`, so it outranks the row's
            // own older page-turn progress in the whole-row sync merge — see
            // markBookDeleted for what broke without that (#6663).
            markBookDeleted(book, Date.now(), deleteAction === 'purge');
          } else {
            // "Remove from Device Only" must never leave stale authorization
            // from an older delete/re-import cycle on the live row.
            book.fileSyncDeletionRequestedAt = null;
          }
          await updateBook(envConfig, book);
          clearBookData(book.hash);
          if (syncBooks) pushLibrary();
        }

        // Cloud deletion. The transfer queue only speaks to Minerva storage, so a
        // book whose cloud copy lives on the selected third-party provider must
        // not be routed through it — it would delete nothing. 'both' / 'purge'
        // tombstoned the book above, and the file sync GCs a tombstoned book's
        // remote directory on its next run, so the removal is already covered.
        // ("Remove from Cloud Only" is not offered for those providers — see
        // BookDetailModal.)
        if (deleteAction === 'cloud' || deleteAction === 'both' || deleteAction === 'purge') {
          if (isMinervaCloudStorageActive(useSettingsStore.getState().settings)) {
            const transferId = transferManager.queueDelete(book, 1, true);
            if (!transferId) {
              throw new Error('Failed to queue cloud deletion');
            }
          } else {
            book.uploadedAt = null;
            await updateBook(envConfig, book);
          }
        }

        eventDispatcher.dispatch('toast', {
          type: 'info',
          timeout: 1000,
          message: deletionMessages[deleteAction],
        });
        return true;
      } catch {
        eventDispatcher.dispatch('toast', {
          message: deletionFailMessages[deleteAction],
          type: 'error',
        });
        return false;
      }
    };
  };

  // Audiobookshelf offline downloads (#6256): the shelf's context menu asks
  // through events so the handlers need not be threaded through every shelf.
  // Removing the copy is "Remove from Device Only".
  const { handleBookOfflineDownload, handleBooksOfflineDownload, offlinePremiumLabel } =
    useAbsOfflineDownload();
  const offlineHandlersRef = useRef({
    download: handleBooksOfflineDownload,
    remove: handleBookDelete('local'),
  });
  offlineHandlersRef.current = {
    download: handleBooksOfflineDownload,
    remove: handleBookDelete('local'),
  };
  useEffect(() => {
    // `books` from a select-mode bulk Download, `book` from a context menu.
    const onDownload = (event: CustomEvent) => {
      offlineHandlersRef.current.download(event.detail.books ?? [event.detail.book]);
    };
    const onRemove = async (event: CustomEvent) => {
      await offlineHandlersRef.current.remove(event.detail.book);
    };
    eventDispatcher.on('abs-offline-download', onDownload);
    eventDispatcher.on('abs-offline-remove', onRemove);
    return () => {
      eventDispatcher.off('abs-offline-download', onDownload);
      eventDispatcher.off('abs-offline-remove', onRemove);
    };
  }, []);

  const handleUpdateMetadata = (book: Book, metadata: BookMetadata, tags: string[]) =>
    saveBookMetadataEdit(envConfig, book, metadata, tags, !!user);

  const handleMetadataValueClick = (type: 'tag' | 'subject', value: string) => {
    const groupBy = type === 'tag' ? LibraryGroupByType.Tag : LibraryGroupByType.Subject;
    const targetGroup = createBookGroups(
      libraryBooks.filter((book) => !book.deletedAt),
      groupBy,
    ).find((item): item is BooksGroup => 'books' in item && item.name === value);
    if (!targetGroup) return;
    const params = new URLSearchParams(window.location.search);
    params.set('groupBy', groupBy);
    params.set('group', targetGroup.id);
    params.delete('shelf');
    params.delete('q');
    setShowDetailsBook(null);
    navigateToLibrary(router, params.toString());
  };

  const getImportTargetGroupId = () => {
    const groupBy = getActiveBookshelfGroupBy(settings, searchParams);
    return groupBy === LibraryGroupByType.Group ? searchParams?.get('group') || '' : '';
  };

  const handleImportBooksFromFiles = async () => {
    setIsSelectMode(false);
    console.log('Importing books from files...');
    if (appService?.isAndroidApp) {
      // The dialog plugin's promise dies when Android tears down the activity
      // or process while the picker is in the foreground (#1217). Open the
      // native-bridge picker fire-and-forget instead; results arrive through
      // the replayable `file-picker-result` event consumed below.
      showFilePicker().catch((err) => console.error('Failed to open file picker:', err));
      return;
    }
    selectFiles({ type: 'books', multiple: true }).then((result) => {
      if (result.files.length === 0 || result.error) return;
      importBooks(result.files, getImportTargetGroupId());
    });
  };

  useAndroidPickedBooks(appService, (files) => {
    importBooks(files, getImportTargetGroupId());
  });

  const handleClipWebPage = async (page: WebBrowserPage) => {
    setIsSelectMode(false);
    const book = await convertToEpubWithWorker({ kind: 'page', ...page });
    await importBooks([{ file: book.file }], searchParams?.get('group') || '');
  };

  // The dialog fetches the chapter list and assembles the EPUB itself
  // (with its own progress/cancel UI) — from here on the built file takes
  // exactly the local-file import path, same as a clipped page.
  const handleImportNovelFile = async (file: File) => {
    setIsSelectMode(false);
    const groupId = searchParams?.get('group') || '';
    await importBooks([{ file }], groupId);
  };

  const handleImportPastedText = async (file: File) => {
    setIsSelectMode(false);
    const { importedCount } = await importBooks([{ file }], getImportTargetGroupId());
    return importedCount > 0;
  };

  const handleImportBooksFromDirectory = async (dirPath?: string) => {
    if (!appService || !isTauriAppPlatform()) return;

    setIsSelectMode(false);

    // When a path is supplied (e.g. URL ingress / drag-drop replay) we
    // honour the legacy "import everything" behaviour without opening
    // the dialog. Manual menu invocations always go through the dialog
    // so users can pick formats and a size threshold before scanning.
    if (dirPath) {
      await runFolderImport({
        directory: dirPath,
        extensions: SUPPORTED_BOOK_EXTS.slice(),
        // The non-dialog path is invoked by URL ingress / drag-drop
        // replay, where the user never picked any filter — keep the
        // synthetic values minimal and non-restrictive.
        selectedGroupIds: [],
        minSizeKB: 0,
        flatten: false,
        // URL ingress / drag-drop don't go through the dialog, so no
        // user expressed an in-place choice here — pass the folder's
        // actual registration state. A registered root stays registered
        // (register is a no-op) and keeps importing in place; anything
        // else keeps the legacy "copy" behaviour (unregister is a
        // no-op). A blanket `false` would silently unregister a
        // registered root now that `runFolderImport` treats OFF as
        // "stop reading this folder in place" (#5680).
        readInPlace: isRegisteredExternalRoot(dirPath),
        // Non-dialog path never opts into auto-import.
        autoImport: false,
      });
      return;
    }

    // Restore both the last-used folder and the last folder-structure
    // mode from localStorage. Anything else (or first-time use) falls
    // back to the dialog's built-in defaults.
    const ls = typeof window !== 'undefined' ? window.localStorage : null;
    const storedDirectory = ls?.getItem(LAST_IMPORT_FOLDER_KEY) || '';
    const storedMode = ls?.getItem(LAST_IMPORT_FOLDER_MODE_KEY);
    const storedFormats = ls?.getItem(LAST_IMPORT_FOLDER_FORMATS_KEY);
    const storedMinSize = ls?.getItem(LAST_IMPORT_FOLDER_MIN_SIZE_KEY);
    const storedReadInPlace = ls?.getItem(LAST_IMPORT_FOLDER_READ_IN_PLACE_KEY);
    const parsedFormats = storedFormats
      ? storedFormats
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : undefined;
    const parsedMinSize =
      storedMinSize !== null && storedMinSize !== undefined
        ? Number.parseInt(storedMinSize, 10)
        : undefined;
    setImportFromFolderState({
      initialDirectory: storedDirectory,
      initialFolderMode: storedMode === 'flatten' ? 'flatten' : 'keep',
      initialSelectedGroupIds: parsedFormats,
      initialMinSizeKB:
        parsedMinSize !== undefined && Number.isFinite(parsedMinSize) && parsedMinSize >= 0
          ? parsedMinSize
          : undefined,
      initialReadInPlace: storedReadInPlace === '1',
      initialAutoImport: isAutoImportFolder(storedDirectory),
    });
  };

  /**
   * Pop the platform's native folder picker. Wrapped here (rather than
   * inlined into the dialog) so the same Android-permission / Tauri
   * dialog dance is shared between the dialog's "change folder" button
   * and any future programmatic import paths.
   */
  const pickImportDirectory = async (): Promise<string | undefined> => {
    if (!appService) return undefined;
    // Both mobile platforms now go through the native-bridge picker:
    // Android dispatches ACTION_OPEN_DOCUMENT_TREE, iOS presents
    // UIDocumentPickerViewController(forOpeningContentTypes: [.folder]).
    // Tauri's bundled dialog plugin still rejects mobile folder picks
    // with "FolderPickerNotImplemented", so the native-bridge route is
    // the only working path on either OS.
    let picked: string | undefined;
    if (appService.isAndroidApp || appService.isIOSApp) {
      // Android needs MANAGE_EXTERNAL_STORAGE for absolute-path reads;
      // iOS doesn't have an equivalent gate (the OS picker is itself
      // the permission grant), so the prompt is Android-only.
      if (appService.isAndroidApp && !(await requestStoragePermission())) return undefined;
      const response = await selectDirectory();
      picked = response.path || undefined;
    } else {
      picked = (await appService.selectDirectory?.('read')) || undefined;
    }
    if (picked && !validatePickedDirectory(picked)) {
      // Already toasted from inside the validator. Treat as "no
      // selection" so the caller leaves the dialog's old folder
      // value alone and the user can immediately try again.
      return undefined;
    }
    return picked;
  };

  /**
   * Sanity-check a path returned by the native folder picker before
   * we commit to scanning it. iOS in particular hands back POSIX paths
   * for "virtual" Files-app entries (the "On My iPhone" root, "Recents",
   * etc.) where {@link readDirectory} will then fail with a Tauri
   * fs_scope rejection. There's no way to disable those entries in the
   * picker itself, so we accept the pick, detect the known-bad shapes,
   * and show a clear toast asking the user to drill into a real
   * subfolder. Returns true if the path looks usable.
   */
  const validatePickedDirectory = (path: string): boolean => {
    if (!appService?.isIOSApp) return true;
    // iOS Files exposes "On My iPhone" as a virtual aggregator over
    // every app's `LSSupportsOpeningDocumentsInPlace` container. When
    // the user picks that root, the picker hands us a path whose
    // basename is exactly `File Provider Storage` (the placeholder
    // directory inside our own App Group container that the system
    // uses to materialise external file-provider contents on demand).
    // POSIX reads against it return either nothing or EPERM, and the
    // Tauri fs_scope refuses it outright because it's outside our
    // allowed globs. Drilling into a concrete subfolder produces a
    // normal, readable POSIX path, which is the path we want.
    //
    // These string anchors aren't localized — iOS keeps the on-disk
    // path in English regardless of the device language, so the
    // basename / segment match is stable.
    const trimmed = path.replace(/\/+$/, '');
    const basename = trimmed.split('/').pop() ?? '';
    const isOnMyIPhoneRoot = basename === 'File Provider Storage';
    if (isOnMyIPhoneRoot) {
      eventDispatcher.dispatch('toast', {
        type: 'warning',
        timeout: 6000,
        message: _(
          'iOS doesn\'t allow importing the "On My iPhone" root. Open it and pick a specific subfolder (e.g. Minerva, Downloads), then try again.',
        ),
      });
      return false;
    }
    return true;
  };

  /**
   * Normalize a path the same way `shouldImportInPlace` does so the
   * predicate / store helpers below stay consistent with the ingest
   * layer's own path-prefix matching. Trailing separators and Windows
   * backslashes are normalized; nothing else is touched.
   */
  const normalizeRoot = (p: string): string => p.replace(/\\/g, '/').replace(/\/+$/, '');

  /**
   * `true` when `directory` is already in
   * `settings.externalLibraryFolders` after path normalization. Hands
   * over to the ImportFromFolderDialog so it can render the "Read in
   * place" toggle as ON-and-locked when the user re-imports from a
   * folder they've already registered. The match is exact-string
   * (after normalization) — sub-paths of a registered folder are NOT
   * considered registered roots themselves, only the registered root
   * is.
   */
  const isRegisteredExternalRoot = (directory: string): boolean => {
    const target = normalizeRoot(directory);
    if (!target) return false;
    const roots = settings.externalLibraryFolders ?? [];
    return roots.some((r) => normalizeRoot(r) === target);
  };

  /**
   * `true` when `directory` is in `settings.autoImportFolders` after path
   * normalization. Seeds the dialog's "Auto-import new books from this
   * folder" checkbox so re-opening on a watched folder shows it ticked.
   */
  const isAutoImportFolder = (directory: string): boolean => {
    const target = normalizeRoot(directory);
    if (!target) return false;
    const roots = settings.autoImportFolders ?? [];
    return roots.some((r) => normalizeRoot(r) === target);
  };

  /**
   * `true` when auto-imports from `directory` should go straight to the library
   * root because the user imported it with "Import all into library". Read from
   * the live store: the scan runs long after the dialog wrote the setting, and
   * the component closure can still hold the pre-write snapshot. A folder
   * watched before this list existed isn't in it and therefore keeps the
   * dialog's default, "Create groups from subfolders".
   */
  /**
   * The watched folders as the Import-from-Folder dialog's management sub-page
   * wants them. Derived from `settings` (not the store snapshot) so removing or
   * re-pointing a folder re-renders the list immediately.
   */
  const watchedFolders = (settings.autoImportFolders ?? []).map((path) => ({
    path,
    flatten: (settings.autoImportFlattenFolders ?? []).some(
      (r) => normalizeRoot(r) === normalizeRoot(path),
    ),
  }));

  const isFlattenedAutoImportFolder = (directory: string): boolean => {
    const target = normalizeRoot(directory);
    if (!target) return false;
    const roots = useSettingsStore.getState().settings.autoImportFlattenFolders ?? [];
    return roots.some((r) => normalizeRoot(r) === target);
  };

  /**
   * Add `directory` to `settings.externalLibraryFolders` (and persist
   * settings) so the ingest layer's `shouldImportInPlace` will pick
   * up subsequent imports from the same folder automatically. No-op
   * when the folder is already registered. Errors are swallowed
   * because the import flow can still succeed in copy mode even if
   * registration fails — we just won't get the in-place behaviour
   * next launch.
   */
  const registerExternalLibraryFolder = async (directory: string): Promise<void> => {
    const target = normalizeRoot(directory);
    if (!target) return;
    const liveSettings = useSettingsStore.getState().settings;
    const existing = liveSettings.externalLibraryFolders ?? [];
    if (existing.some((r) => normalizeRoot(r) === target)) {
      return;
    }
    const next = [...existing, directory];
    const nextSettings = { ...liveSettings, externalLibraryFolders: next };
    setSettings(nextSettings);
    try {
      await saveSettings(envConfig, nextSettings);
    } catch (e) {
      console.error('Failed to persist externalLibraryFolders update:', e);
    }
  };

  /**
   * Remove `directory` from `settings.externalLibraryFolders` (and persist
   * settings) — the symmetric counterpart of
   * {@link registerExternalLibraryFolder}, run when the user unchecks "Read
   * books in place" for a registered folder (#5680). Subsequent imports from
   * the folder copy books into Books/<hash>/ again; books previously imported
   * in place keep working (the reader falls back to `book.filePath`) and are
   * converted to managed copies as re-imports encounter them. A no-op when
   * the folder isn't registered.
   */
  const unregisterExternalLibraryFolder = async (directory: string): Promise<void> => {
    const target = normalizeRoot(directory);
    if (!target) return;
    const liveSettings = useSettingsStore.getState().settings;
    const existing = liveSettings.externalLibraryFolders ?? [];
    const next = existing.filter((r) => normalizeRoot(r) !== target);
    if (next.length === existing.length) return;
    const nextSettings = { ...liveSettings, externalLibraryFolders: next };
    setSettings(nextSettings);
    try {
      await saveSettings(envConfig, nextSettings);
    } catch (e) {
      console.error('Failed to persist externalLibraryFolders update:', e);
    }
  };

  /**
   * Add or remove `directory` from `settings.autoImportFolders` (and persist)
   * per the user's per-folder "Auto-import new books from this folder" choice.
   * `flatten` records the same import's "Folder Structure" pick so later scans
   * can group newly-found books exactly like this import did — it is tracked in
   * a parallel list because only flattened folders need an entry. A no-op when
   * the folder is already in the desired state. Errors are swallowed — the
   * import itself still succeeds; we just won't watch (or stop watching) the
   * folder until the next successful settings write.
   */
  const setAutoImportFolder = async (
    directory: string,
    enabled: boolean,
    flatten: boolean,
  ): Promise<void> => {
    const target = normalizeRoot(directory);
    if (!target) return;
    const liveSettings = useSettingsStore.getState().settings;
    const existing = liveSettings.autoImportFolders ?? [];
    const existingFlatten = liveSettings.autoImportFlattenFolders ?? [];
    const present = existing.some((r) => normalizeRoot(r) === target);
    const flattenPresent = existingFlatten.some((r) => normalizeRoot(r) === target);
    const flattenWanted = enabled && flatten;
    if (enabled === present && flattenWanted === flattenPresent) return;
    // Append only when the folder isn't listed yet: re-adding an existing entry
    // would move it to the end and shuffle the Watched Folders list under the
    // user's finger every time they flip a row's structure.
    const without = (roots: string[]) => roots.filter((r) => normalizeRoot(r) !== target);
    const next = enabled ? (present ? existing : [...existing, directory]) : without(existing);
    const nextFlatten = flattenWanted
      ? flattenPresent
        ? existingFlatten
        : [...existingFlatten, directory]
      : without(existingFlatten);
    const nextSettings = {
      ...liveSettings,
      autoImportFolders: next,
      autoImportFlattenFolders: nextFlatten,
    };
    setSettings(nextSettings);
    try {
      await saveSettings(envConfig, nextSettings);
    } catch (e) {
      console.error('Failed to persist autoImportFolders update:', e);
    }
  };

  /**
   * Recursively scan {@link result.directory}, keep files matching one
   * of {@link result.extensions} that are at least
   * {@link result.minSizeKB} KB, and feed them through {@link importBooks}.
   *
   * Two cooperating signals carry "where should the imported books
   * end up" downstream:
   *   1. Each {@link SelectedFile}'s `basePath` — when present,
   *      {@link importBooks}' `processFile` derives a nested groupName
   *      relative to it (`<sub>` / `<sub>/<deeper>`).
   *   2. The `groupId` argument passed to {@link importBooks} —
   *      tri-state per the comment in `processFile`. An explicit
   *      string (including '') wins over basePath-derived grouping.
   *
   * The two flatten/keep modes use these signals as follows:
   *   - keep    → omit basePath? no, *include* basePath; pass
   *               groupId=undefined so basePath wins.
   *   - flatten → omit basePath AND pass an explicit groupId equal to
   *               the user's currently-viewed group ('' = root). The
   *               omitted basePath alone wouldn't be enough on a
   *               re-import, since deduped books carry stale groupIds
   *               from prior sessions; the explicit groupId is what
   *               actually reseats them. Dropping basePath in flatten
   *               mode is therefore belt-and-suspenders.
   */
  const runFolderImport = async (result: ImportFromFolderResult) => {
    if (!appService || !result.directory) return;
    // Last-chance sanity check. The dialog's own pickImportDirectory
    // already validates fresh picks, but `result.directory` can also
    // come from the persisted "last import folder" in localStorage —
    // which may have been a bad path (e.g. user picked "On My iPhone"
    // root last session, app remembered it, user just hits OK now).
    // Catch that here so they get the same clear guidance instead of
    // a fs_scope error from readDirectory below.
    if (!validatePickedDirectory(result.directory)) return;

    // The user can opt the chosen folder into "in place" via the
    // dialog toggle; the same effect happens automatically when the
    // folder is already a registered external library folder (the
    // ingest layer's `shouldImportInPlace` does a path-prefix match
    // against `settings.externalLibraryFolders`). Register here so the
    // bookkeeping survives across launches and so subsequent imports
    // from the same folder don't have to re-trigger the toggle. The
    // OFF branch unregisters so unchecking the box on a registered
    // folder turns in-place mode off again (#5680) — callers that
    // bypass the dialog must pass the folder's actual registration
    // state, not a blanket `false` (see handleImportBooksFromDirectory).
    if (result.readInPlace) {
      await registerExternalLibraryFolder(result.directory);
    } else {
      await unregisterExternalLibraryFolder(result.directory);
    }
    // Opt this folder into (or out of) auto-import per the dialog's per-folder
    // checkbox. `result.autoImport` already implies `readInPlace` (the dialog
    // gates it), so registration above has run; unchecking removes the folder
    // from the watched set while leaving it registered as read-in-place.
    await setAutoImportFolder(result.directory, result.autoImport, result.flatten);

    // Re-grant scopes for the directory before scanning. This matters
    // when `result.directory` came from somewhere the dialog plugin
    // didn't authorise — typically the persisted "last import folder"
    // restored from localStorage when the user just hit OK without
    // re-picking. Without this, `RemoteFile` reads through the asset
    // protocol later in `importBook` would fail with
    // "asset protocol not configured to allow the path".
    await appService.allowPathsInScopes?.([result.directory], true);
    const exts = result.extensions.map((e) => e.toLowerCase());
    const minSizeBytes = Math.max(0, Math.floor(result.minSizeKB)) * 1024;
    let files;
    try {
      files = await appService.readDirectory(result.directory, 'None', exts);
    } catch (e) {
      // readDirectory can reject for a few related reasons:
      //   - iOS handed us a virtual / file-provider path that the OS
      //     sandbox refuses to enumerate (the validator above catches
      //     the common shapes, but not every file-provider variant);
      //   - the path is outside Tauri's `fs_scope` and scope
      //     extension didn't stick (e.g. an iCloud Drive entry whose
      //     security-scoped resource the system declined to grant);
      //   - the directory was deleted / permissions revoked between
      //     pick and scan.
      // Swallow the rejection (otherwise it bubbles up as an
      // unhandledRejection through Next.js) and surface a friendly
      // message that nudges the user to re-pick.
      const detail = e instanceof Error ? e.message : String(e);
      console.error('Folder import: readDirectory failed', detail);
      const isIOS = !!appService.isIOSApp;
      eventDispatcher.dispatch('toast', {
        type: 'error',
        timeout: 6000,
        message: isIOS
          ? _(
              'Couldn\'t read this folder. Some iOS locations (like the "On My iPhone" root or iCloud Drive top-level) can\'t be scanned — please pick a specific subfolder and try again.',
            )
          : _(
              "Couldn't read this folder. Please pick the folder again, or choose a different location.",
            ),
      });
      return;
    }
    // Re-filter by extension because the JS fallback of readDirectory ignores
    // the extensions argument (only the native Rust walk filters in-scan).
    const filtered = files.filter((file) => {
      if (isInHiddenDir(file.path)) return false;
      const ext = file.path.split('.').pop()?.toLowerCase() || '';
      if (!exts.includes(ext)) return false;
      if (minSizeBytes > 0 && file.size < minSizeBytes) return false;
      return true;
    });
    const entries = filtered.map((file) => ({
      fullPath: joinScannedPath(result.directory, file.path),
      size: file.size,
    }));
    // Same mapping the auto-import scan uses, so a folder's later scans group
    // newly-found books exactly like this import does.
    const toImportFiles = toWatchedFolderImports(result.directory, entries, result.flatten);
    if (toImportFiles.length === 0) {
      eventDispatcher.dispatch('toast', {
        type: 'info',
        message: _('No matching books found in the selected folder.'),
      });
      return;
    }
    // When flattening, route the books into whichever group the user
    // is currently viewing (empty string == library root). When
    // preserving structure we leave groupId undefined so importBooks
    // derives nested groupNames from each file's basePath.
    const targetGroupId = result.flatten ? searchParams?.get('group') || '' : undefined;
    importBooks(toImportFiles, targetGroupId);
  };

  const handleSetSelectMode = (selectMode: boolean) => {
    if (selectMode && appService?.hasHaptics) {
      impactFeedback('medium');
    }
    setIsSelectMode(selectMode);
    setIsSelectAll(false);
    setIsSelectNone(false);
  };

  const updateLibrarySearchUrl = (target: LibrarySearchTarget, config: LibrarySearchConfig) => {
    const params = new URLSearchParams(window.location.search);
    const query = pendingLibrarySearchQueryRef.current ?? librarySearchQuery;
    if (query) params.set('q', query);
    else params.delete('q');
    if (target === 'text') params.set('search', 'text');
    else params.delete('search');
    if (config.mode !== 'contains') params.set('mode', config.mode);
    else params.delete('mode');
    if (config.matchCase) params.set('matchCase', 'true');
    else params.delete('matchCase');
    if (config.matchDiacritics) params.set('matchDiacritics', 'true');
    else params.delete('matchDiacritics');
    if (config.mode === 'nearby-words' && config.nearbyWords !== DEFAULT_NEARBY_WORDS) {
      params.set('nearby', String(config.nearbyWords));
    } else {
      params.delete('nearby');
    }
    const value = params.toString();
    window.history.replaceState(null, '', `?${value}`);
    sessionStorage.setItem('lastLibraryParams', value);
  };

  const handleSearchTargetChange = (target: LibrarySearchTarget) => {
    librarySearchTargetRef.current = target;
    setLibrarySearchTarget(target);
    debouncedSearchUrlUpdate.cancel();
    updateLibrarySearchUrl(target, librarySearchConfigRef.current);
    if (target === 'text') handleSetSelectMode(false);
  };

  // The input itself stays instant; applying the query (URL, shelf filter,
  // content scans) debounces so typing does not re-filter the library or
  // restart searches on every keystroke.
  const updateLibrarySearchUrlRef = useRef<typeof updateLibrarySearchUrl>(null!);
  updateLibrarySearchUrlRef.current = updateLibrarySearchUrl;
  const debouncedSearchUrlUpdate = React.useMemo(
    () =>
      debounce(() => {
        updateLibrarySearchUrlRef.current(
          librarySearchTargetRef.current,
          librarySearchConfigRef.current,
        );
      }, 500),
    [],
  );
  useEffect(() => () => debouncedSearchUrlUpdate.cancel(), [debouncedSearchUrlUpdate]);

  // Immediate variant for history pills and other one-shot applications.
  const handleSearchQueryApply = (query: string) => {
    debouncedSearchUrlUpdate.cancel();
    const urlQuery = new URLSearchParams(window.location.search).get('q') ?? '';
    pendingLibrarySearchQueryRef.current = query === urlQuery ? null : query;
    setLibrarySearchQuery(query);
    updateLibrarySearchUrl(librarySearchTargetRef.current, librarySearchConfigRef.current);
  };

  const handleSearchQueryChange = (query: string) => {
    const urlQuery = new URLSearchParams(window.location.search).get('q') ?? '';
    pendingLibrarySearchQueryRef.current = query === urlQuery ? null : query;
    setLibrarySearchQuery(query);
    debouncedSearchUrlUpdate();
  };

  const handleSearchConfigChange = (config: LibrarySearchConfig) => {
    librarySearchConfigRef.current = config;
    React.startTransition(() => {
      setLibrarySearchConfig(config);
    });
    debouncedSearchUrlUpdate.cancel();
    updateLibrarySearchUrl(librarySearchTargetRef.current, config);
  };

  const handleSelectAll = () => {
    setIsSelectAll(true);
    setIsSelectNone(false);
  };

  const handleDeselectAll = () => {
    setIsSelectNone(true);
    setIsSelectAll(false);
  };

  const handleShowDetailsBook = (book: Book) => {
    setShowDetailsBook(book);
  };

  const handleNavigateToPath = (path: string | undefined) => {
    const group = path ? getGroupId(path) || '' : '';
    setIsSelectAll(false);
    setIsSelectNone(false);
    handleLibraryNavigation(group);
  };

  if (
    !appService ||
    !insets ||
    checkOpenWithBooks ||
    checkLastOpenBooks ||
    checkPendingLaunchLink
  ) {
    return <div className='full-height bg-base-200' />;
  }

  const showBookshelf = libraryLoaded || libraryBooks.length > 0;

  return (
    <div
      ref={pageRef}
      aria-label={_('Your Library')}
      className={clsx(
        'library-page text-base-content full-height flex select-none flex-col overflow-hidden',
        viewSettings?.isEink ? 'bg-base-100' : 'bg-base-200',
        appService?.hasRoundedWindow && isRoundedWindow && 'window-border rounded-window',
      )}
    >
      <div
        className='relative top-0 z-40 w-full'
        role='banner'
        tabIndex={-1}
        aria-label={_('Library Header')}
      >
        <LibraryHeader
          isSelectMode={isSelectMode}
          isSelectAll={isSelectAll}
          onPullLibrary={pullLibrary}
          onImportBooksFromFiles={handleImportBooksFromFiles}
          onImportPastedText={() => setShowImportPastedText(true)}
          onImportBooksFromDirectory={
            appService?.canReadExternalDir ? handleImportBooksFromDirectory : undefined
          }
          onImportFromWebBrowser={isTauriAppPlatform() ? () => setShowWebSources(true) : undefined}
          onImportBookFromNovelUrl={
            isTauriAppPlatform() ? () => setShowImportNovel(true) : undefined
          }
          onOpenCatalogManager={handleShowOPDSDialog}
          onOpenFeeds={handleShowFeeds}
          onToggleSelectMode={() => handleSetSelectMode(!isSelectMode)}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          searchQuery={librarySearchQuery}
          searchTarget={librarySearchTarget}
          searchConfig={librarySearchConfig}
          onSearchConfigChange={handleSearchConfigChange}
          onSearchQueryChange={handleSearchQueryChange}
          onSearchTargetChange={handleSearchTargetChange}
        />
        <progress
          aria-label={_('Library Search Progress')}
          aria-hidden={librarySearchProgress != null ? 'false' : 'true'}
          className={clsx(
            'progress progress-success absolute bottom-0 left-0 right-0 h-1 translate-y-[2px] transition-opacity duration-200 sm:translate-y-[4px]',
            librarySearchProgress != null ? 'opacity-100' : 'opacity-0',
          )}
          value={librarySearchProgress ?? 0}
          max={100}
        />
        <progress
          aria-label={_('Library Sync Progress')}
          aria-hidden={isSyncing ? 'false' : 'true'}
          className={clsx(
            'progress progress-success absolute bottom-0 left-0 right-0 h-1 translate-y-[2px] transition-opacity duration-200 sm:translate-y-[4px]',
            isSyncing ? 'opacity-100' : 'opacity-0',
          )}
          value={syncProgress * 100}
          max='100'
        />
      </div>
      {(loading || isSyncing) && (
        <div className='fixed inset-0 z-50 flex items-center justify-center'>
          <Spinner loading />
        </div>
      )}
      {librarySearchTarget === 'text' &&
        !librarySearchQuery.trim() &&
        librarySearchHistory.length > 0 && (
          <div className='relative my-1 flex shrink-0 items-center px-4 sm:px-6'>
            <div className='no-scrollbar not-eink:[mask-image:linear-gradient(to_right,transparent,black_12px,black_calc(100%_-_12px),transparent)] flex flex-1 gap-1.5 overflow-x-auto'>
              {librarySearchHistory.map((term) => (
                <button
                  key={term}
                  type='button'
                  onClick={() => handleSearchQueryApply(term)}
                  className='bg-base-300/45 hover:bg-base-300/70 text-base-content/70 max-w-[60%] shrink-0 whitespace-nowrap rounded-full px-3 py-0.5 text-xs'
                >
                  <p className='truncate'>{term}</p>
                </button>
              ))}
            </div>
            <button
              type='button'
              onClick={() => {
                clearLibrarySearchHistory();
                setLibrarySearchHistory([]);
              }}
              title={_('Clear search history')}
              aria-label={_('Clear search history')}
              className='text-base-content/50 hover:text-base-content/80 flex h-6 w-8 shrink-0 items-center justify-center'
            >
              <MdClose className='h-4 w-4' />
            </button>
          </div>
        )}
      {currentGroupPath && (
        <div
          className={`transition-all duration-300 ease-in-out ${
            currentGroupPath ? 'opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className='flex flex-wrap items-center gap-y-1 px-4 text-base'>
            <button
              onClick={() => handleNavigateToPath(undefined)}
              className='hover:bg-base-300 text-base-content/85 rounded-sm px-2 py-1'
            >
              {_('All')}
            </button>
            {getBreadcrumbs(currentGroupPath).map((crumb, index, array) => {
              const isLast = index === array.length - 1;
              return (
                <React.Fragment key={index}>
                  <MdChevronRight size={iconSize} className='text-neutral-content' />
                  {isLast ? (
                    <span className='truncate rounded-sm px-2 py-1'>{crumb.name}</span>
                  ) : (
                    <button
                      onClick={() => handleNavigateToPath(crumb.path)}
                      className='hover:bg-base-300 text-base-content/85 truncate rounded-sm px-2 py-1'
                    >
                      {crumb.name}
                    </button>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}
      {currentVirtualGroup && (
        <GroupHeader
          groupBy={currentVirtualGroup.groupBy}
          groupName={currentVirtualGroup.groupName}
          localized={currentVirtualGroup.localized}
        />
      )}
      <BookshelvesDialog />
      {showBookshelf && (
        <div aria-label={_('Your Bookshelf')} className='flex min-h-0 grow flex-col'>
          <div
            ref={containerRef}
            className={clsx(
              'scroll-container drop-zone flex min-h-0 grow flex-col',
              isDragging && 'drag-over',
            )}
            style={{
              paddingRight: `${insets.right}px`,
              paddingLeft: `${insets.left}px`,
            }}
          >
            <DropIndicator />
            <Bookshelf
              libraryBooks={libraryBooks}
              isSelectMode={isSelectMode}
              isSelectAll={isSelectAll}
              isSelectNone={isSelectNone}
              onScrollerRef={handleScrollerRef}
              handleImportBooks={setImportMenuAnchor}
              handleBookUpload={handleBookUpload}
              handleBookDownload={handleBookDownload}
              handleBookDelete={handleBookDelete('both')}
              handleBookPurge={handleBookDelete('purge')}
              handleSetSelectMode={handleSetSelectMode}
              handleShowDetailsBook={handleShowDetailsBook}
              handleLibraryNavigation={handleLibraryNavigation}
              booksTransferProgress={booksTransferProgress}
              handlePushLibrary={pushLibrary}
              onSearchContents={() => handleSearchTargetChange('text')}
              onSearchProgress={setLibrarySearchProgress}
              contentSearch={
                librarySearchTarget === 'text'
                  ? { query: searchParams?.get('q') ?? '', config: librarySearchConfig }
                  : null
              }
            />
          </div>
        </div>
      )}
      {importMenuAnchor && (
        <ImportMenuPopup
          anchor={importMenuAnchor}
          onClose={() => setImportMenuAnchor(null)}
          onImportBooksFromFiles={handleImportBooksFromFiles}
          onImportPastedText={() => setShowImportPastedText(true)}
          onImportBooksFromDirectory={
            appService?.canReadExternalDir ? handleImportBooksFromDirectory : undefined
          }
          onImportFromWebBrowser={isTauriAppPlatform() ? () => setShowWebSources(true) : undefined}
          onImportBookFromNovelUrl={
            isTauriAppPlatform() ? () => setShowImportNovel(true) : undefined
          }
          onOpenCatalogManager={handleShowOPDSDialog}
          onOpenFeeds={handleShowFeeds}
        />
      )}
      <NowPlayingBar isSelectMode={isSelectMode} />
      {showDetailsBook && (
        <BookDetailModal
          isOpen={!!showDetailsBook}
          book={showDetailsBook}
          onClose={() => setShowDetailsBook(null)}
          handleBookUpload={handleBookUpload}
          handleBookDownload={handleBookDownload}
          handleBookDelete={handleBookDelete('both')}
          // Minerva storage only. A third-party provider mirrors the library, so
          // removing just its cloud copy is not expressible: the next sync would
          // upload the still-local book straight back (#5084).
          handleBookDeleteCloudBackup={
            isMinervaCloudStorageActive(settings) ? handleBookDelete('cloud') : undefined
          }
          handleBookDeleteLocalCopy={handleBookDelete('local')}
          handleBookPurge={handleBookDelete('purge')}
          handleBookMetadataUpdate={handleUpdateMetadata}
          handleBookOfflineDownload={isTauriAppPlatform() ? handleBookOfflineDownload : undefined}
          offlinePremiumLabel={offlinePremiumLabel}
          onMetadataValueClick={handleMetadataValueClick}
        />
      )}
      {isTransferQueueOpen && (
        <ModalPortal>
          <TransferQueuePanel />
        </ModalPortal>
      )}
      <AboutWindow />
      <ReadingStatsDialog />
      <KeyboardShortcutsHelp />
      <LocalSendManager />
      <UpdaterWindow />
      <MigrateDataWindow />
      <BackupWindow onPullLibrary={pullLibrary} />
      <CacheManagerWindow />
      {isSettingsDialogOpen && <SettingsDialog bookKey={''} />}
      {showCatalogManager && <CatalogDialog onClose={handleDismissOPDSDialog} />}
      {showFeeds && <FeedsView onClose={() => setShowFeeds(false)} />}
      <AddFeedModal
        isOpen={showAddFeed}
        onClose={() => setShowAddFeed(false)}
        onSubmit={handleAddFeedSubmit}
      />
      {failedImportsModal && (
        <FailedImportsDialog
          failedImports={failedImportsModal}
          onClose={() => setFailedImportsModal(null)}
        />
      )}
      {importFromFolderState && (
        <ImportFromFolderDialog
          initialDirectory={importFromFolderState.initialDirectory}
          initialFolderMode={importFromFolderState.initialFolderMode}
          initialSelectedGroupIds={importFromFolderState.initialSelectedGroupIds}
          initialMinSizeKB={importFromFolderState.initialMinSizeKB}
          initialReadInPlace={importFromFolderState.initialReadInPlace}
          initialAutoImport={importFromFolderState.initialAutoImport}
          isRegisteredExternalRoot={isRegisteredExternalRoot}
          watchedFolders={watchedFolders}
          onUnwatchFolder={(path) => void setAutoImportFolder(path, false, false)}
          onSetWatchedFolderFlatten={(path, flatten) =>
            void setAutoImportFolder(path, true, flatten)
          }
          onPickDirectory={pickImportDirectory}
          onCancel={() => setImportFromFolderState(null)}
          onConfirm={(result) => {
            setImportFromFolderState(null);
            // Remember the folder + filters for next time. Done here
            // (rather than inside pickImportDirectory) so we only
            // persist values the user actually committed to, not
            // ones they cancelled out of.
            if (typeof window !== 'undefined') {
              if (result.directory) {
                window.localStorage.setItem(LAST_IMPORT_FOLDER_KEY, result.directory);
              }
              window.localStorage.setItem(
                LAST_IMPORT_FOLDER_MODE_KEY,
                result.flatten ? 'flatten' : 'keep',
              );
              if (result.selectedGroupIds.length > 0) {
                window.localStorage.setItem(
                  LAST_IMPORT_FOLDER_FORMATS_KEY,
                  result.selectedGroupIds.join(','),
                );
              }
              window.localStorage.setItem(
                LAST_IMPORT_FOLDER_MIN_SIZE_KEY,
                String(result.minSizeKB),
              );
              window.localStorage.setItem(
                LAST_IMPORT_FOLDER_READ_IN_PLACE_KEY,
                result.readInPlace ? '1' : '0',
              );
            }
            void runFolderImport(result);
          }}
        />
      )}
      <WebSourcesDialog
        isOpen={showWebSources}
        onClose={() => setShowWebSources(false)}
        onClip={handleClipWebPage}
      />
      <WebBrowserImportDialog
        download={pendingWebBrowserDownload}
        onConfirm={() => void confirmWebBrowserDownload()}
        onDismiss={() => void dismissWebBrowserDownload()}
      />
      <ImportNovelDialog
        isOpen={showImportNovel}
        onClose={() => setShowImportNovel(false)}
        onImport={handleImportNovelFile}
      />
      <ImportPastedTextDialog
        isOpen={showImportPastedText}
        onClose={() => setShowImportPastedText(false)}
        onImport={handleImportPastedText}
      />
      <ClipSignInAlert />
      <Toast />
    </div>
  );
};

const LibraryPage = () => {
  return (
    <Suspense fallback={<div className='full-height' />}>
      <LibraryPageWithSearchParams />
    </Suspense>
  );
};

export default LibraryPage;
